import { Badge } from '@/components/ui/badge'
import { type QaOutcome, QaOutcomeCode } from '@/models/internalEmail'

const LABELS: Record<QaOutcomeCode, string> = {
  [QaOutcomeCode.DISPATCHED]: 'Handed off',
  [QaOutcomeCode.STORED_NO_DISPATCH]: 'Stored — no follow-up for this type',
  [QaOutcomeCode.STORED_NO_FOLLOWUP]: 'Stored — another copy took the work',
  [QaOutcomeCode.DROPPED_OWN_INBOX]: 'Dropped — sent by one of our own inboxes',
  [QaOutcomeCode.DROPPED_BOUNCE]: 'Dropped — recorded as a bounce',
  [QaOutcomeCode.DROPPED_UNRESOLVED]: 'Dropped — nothing resolved',
}

/** Branch names the backend reports, phrased for a person reading the thread. */
const HANDLERS: Record<string, string> = {
  application_channel: 'the application channel',
  carrier_notification: 'internal-agent carrier triage',
  email_channel: 'the email agent channel',
  loss_run: 'loss-run collection',
  rocklake_email: 'the Rocklake ingestion pipeline',
  sales_lead: 'the sales-lead recorder',
  sales_lead_captured: 'the sales-lead recorder (matched by sender)',
  support_forward: 'a person (forwarded to support)',
}

/** Branches that claimed the message but dispatched nothing — never "handed to". */
const STORED_ONLY: Record<string, string> = {
  rocklake_stored: 'Stored — Rocklake ingestion is switched off',
  stored_automated: 'Stored — automated sender, no follow-up',
}

const DROPPED = new Set<QaOutcomeCode>([
  QaOutcomeCode.DROPPED_OWN_INBOX,
  QaOutcomeCode.DROPPED_BOUNCE,
  QaOutcomeCode.DROPPED_UNRESOLVED,
])

function label(outcome: QaOutcome): string {
  if (outcome.code !== QaOutcomeCode.DISPATCHED || !outcome.handler) {
    return LABELS[outcome.code]
  }
  return STORED_ONLY[outcome.handler] ?? `Handed to ${HANDLERS[outcome.handler] ?? outcome.handler}`
}

export function QaOutcomeBadge({ outcome }: { outcome: QaOutcome | null }) {
  if (!outcome) return null
  return <Badge variant={DROPPED.has(outcome.code) ? 'destructive' : 'secondary'}>{label(outcome)}</Badge>
}
